import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Drawer,
  Tabs,
  Group,
  Stack,
  Text,
  Button,
  Badge,
  Loader,
  Center,
  Textarea,
  Alert,
  Select,
  ThemeIcon,
  Divider,
  Box,
  Table,
  Chip,
  ScrollArea,
  Modal,
  ActionIcon,
  UnstyledButton,
  Radio,
} from '@mantine/core';
import styles from './PostSessionModal.module.css';
import { notifications } from '@mantine/notifications';
import { AlertCircle, AlertTriangle, Check, CheckCircle, ChevronDown, Clock, Edit3, MessageCircle, X } from 'react-feather';
import {
  coachPortalApi,
  type SessionParticipant,
  type AbsentReason,
  type AttendanceMarkingStatus,
  type ParticipantAttendanceInput,
  type CreateConcernFlagInput,
  type CoachSessionFeedback,
  type EngagementRatingInput,
  type RsvpStatus,
} from '../../../services/api/coachPortal';
import { ConcernFlagsSection } from './ConcernFlagsSection';
import { ParticipantEngagementRating } from './EngagementRatingSection';
import { ENGAGEMENT_RATING_CONFIG, ENGAGEMENT_RATING_MAP } from '../../../config/engagementRatingConfig';
import { SessionDetailHeader } from '../../cohorts/components/sessions/SessionDetailHeader';
import { MyNotesPanel } from './MyNotesPanel';
import { useAuthStore } from '../../../stores/auth';
import {
  getCoachFeedbackQuestionSet,
  type CoachFeedbackQuestion,
} from '../../../config/coachFeedbackQuestionSets';
import { CharacterCountTextarea } from '../../../components/common/CharacterCountTextarea';

interface PostSessionModalProps {
  opened: boolean;
  onClose: () => void;
  sessionId: string | null;
  onSuccess?: () => void;
  // Header context (passed from parent)
  sessionNumber?: number | null;
  topicName?: string | null;
  groupName?: string;
  scheduledStart?: string | null;
  timezone?: string | null;
  totalSessions?: number;
  sessionStatus?: string;
}

// Attendance constants
const ABSENT_REASON_OPTIONS: { value: AbsentReason; label: string }[] = [
  { value: 'business_conflict', label: 'Business Conflict' },
  { value: 'personal_conflict', label: 'Personal Conflict' },
  { value: 'no_notice', label: 'No Notice' },
];

const ATTENDANCE_STATUS_OPTIONS = [
  { value: 'attended', label: 'Attended' },
  { value: 'partial', label: 'Partial' },
  { value: 'absent', label: 'Absent' },
];

const STATUS_COLORS: Record<AttendanceMarkingStatus, string> = {
  attended: 'green',
  partial: 'yellow',
  absent: 'red',
};

// RSVP pill configuration
const RSVP_CONFIG: Record<
  RsvpStatus,
  { label: string; color: string; icon: typeof Check }
> = {
  accepted: { label: 'Accepted', color: 'teal', icon: CheckCircle },
  declined: { label: 'Declined', color: 'red', icon: X },
  pending: { label: 'Pending', color: 'yellow', icon: Clock },
  tentative: { label: 'Pending', color: 'yellow', icon: Clock },
};

// MED-888: reflection question sets moved to src/config/coachFeedbackQuestionSets.ts
// so additional coach-type variants can be added without changing this component.

// Quick-select pill options
const PARTIAL_ATTENDANCE_PILLS = ['Arrived late', 'Left early'];
const ABSENT_PILLS_BY_REASON: Record<AbsentReason, string[]> = {
  business_conflict: ['Meeting conflict', 'Work travel'],
  personal_conflict: ['OOO / Vacation', 'Out sick'],
  no_notice: ['No response', 'Unknown'],
  // MED-794 added these values for the live post-session screen; the legacy
  // PostSessionModal does not surface them as quick-select pills, but they
  // must be present here so the Record<AbsentReason, …> contract holds.
  technical_issues: [],
  other: [],
};
const ALL_ABSENT_PILLS = Object.values(ABSENT_PILLS_BY_REASON).flat();

interface ParticipantAttendanceState {
  status: AttendanceMarkingStatus;
  absent_reason: AbsentReason | null;
  notes: string;
}

function initAttendanceState(participant: SessionParticipant): ParticipantAttendanceState {
  const currentStatus = participant.current_status;
  let status: AttendanceMarkingStatus = 'attended';
  if (currentStatus === 'absent') status = 'absent';
  else if (currentStatus === 'partial') status = 'partial';
  else if (currentStatus === 'attended') status = 'attended';

  return {
    status,
    absent_reason: participant.current_absent_reason || null,
    notes: participant.current_notes || '',
  };
}

export function PostSessionModal({
  opened,
  onClose,
  sessionId,
  onSuccess,
  sessionNumber,
  topicName,
  groupName,
  scheduledStart,
  timezone,
  totalSessions,
  sessionStatus,
}: PostSessionModalProps) {
  const user = useAuthStore((s) => s.user);
  const coachName = user ? `${user.first_name} ${user.last_name}` : null;
  const isPartnerCoach = user?.coach_type === 'partner';
  // MED-732: coach persona (not admin / CM) — used to hide the coach tile and
  // scope mobile drawer layout so CM views stay unchanged.
  const isCoachPersona = user?.primary_persona === 'coach';

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string | null>('attendance');
  const [loading, setLoading] = useState(true);
  const [submittingAttendance, setSubmittingAttendance] = useState(false);
  const [confirmSendSurveys, setConfirmSendSurveys] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [attendanceSaved, setAttendanceSaved] = useState(false);
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [isEditMode, setIsEditMode] = useState(true);

  // Session info (fetched from API)
  const [sessionInfo, setSessionInfo] = useState<{
    session_id: string;
    session_title: string;
    session_number: number | null;
    session_date: string;
    group_id: string;
    group_name: string;
  } | null>(null);

  // Attendance state
  const [participants, setParticipants] = useState<SessionParticipant[]>([]);
  const [attendanceMap, setAttendanceMap] = useState<Map<string, ParticipantAttendanceState>>(
    new Map(),
  );

  // Feedback state
  // MED-888: partner-coach Q5 persists a numeric 1-5; medley-coach answers are all
  // strings. The responses map is widened to accommodate both.
  const [responses, setResponses] = useState<Record<string, string | number>>({});
  const [concernFlags, setConcernFlags] = useState<CreateConcernFlagInput[]>([]);
  const [engagementRatingMap, setEngagementRatingMap] = useState<Map<string, number>>(new Map());
  const [existingFeedback, setExistingFeedback] = useState<CoachSessionFeedback | null>(null);

  // MED-888: pick the question set for the current coach type. Reactive to coach_type
  // changes (e.g., persona switcher) so AC10 — switching personas without page refresh
  // — works without explicit reload.
  const questionSet = useMemo(
    () => getCoachFeedbackQuestionSet(user?.coach_type),
    [user?.coach_type],
  );

  // MED-732: per-participant UI state
  // - notesRevealed: userIds with the notes textarea opened via "+ Add Optional Note"
  // - picker: currently-open mobile picker modal (status / reason / engagement) for a participant
  const [notesRevealed, setNotesRevealed] = useState<Set<string>>(new Set());
  const [picker, setPicker] = useState<{ type: 'status' | 'reason' | 'engagement'; userId: string } | null>(null);
  const closePicker = () => setPicker(null);
  const revealNotes = (userId: string) =>
    setNotesRevealed((prev) => {
      const next = new Set(prev);
      next.add(userId);
      return next;
    });

  // Toggle a quick-note pill value in the participant's notes field.
  // Notes are stored as a comma-separated string; this preserves any
  // manually typed portions while adding/removing the pill string.
  const togglePillInNotes = (userId: string, pill: string) => {
    const current = attendanceMap.get(userId)?.notes ?? '';
    const items = current
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const next = items.includes(pill) ? items.filter((i) => i !== pill) : [...items, pill];
    handleParticipantChange(userId, 'notes', next.join(', '));
  };
  const isPillActive = (userId: string, pill: string): boolean => {
    const notes = attendanceMap.get(userId)?.notes ?? '';
    return notes
      .split(',')
      .map((s) => s.trim())
      .includes(pill);
  };

  // Compute attendance summary
  const summary = useMemo(() => {
    let attended = 0;
    let partial = 0;
    let absent = 0;
    attendanceMap.forEach((state) => {
      if (state.status === 'attended') attended++;
      else if (state.status === 'partial') partial++;
      else if (state.status === 'absent') absent++;
    });
    return { attended, partial, absent };
  }, [attendanceMap]);

  const percentage =
    participants.length > 0
      ? Math.round(((summary.attended + summary.partial) / participants.length) * 100)
      : 0;

  // Header stats
  const headerStats = useMemo(() => {
    const sessionsStr =
      sessionNumber != null && totalSessions != null
        ? `${sessionNumber}/${totalSessions}`
        : sessionNumber != null
          ? String(sessionNumber)
          : '—';

    // Compute average engagement from rating map
    let engagementStr = '—';
    if (engagementRatingMap.size > 0) {
      const ratings = Array.from(engagementRatingMap.values());
      const avg = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
      engagementStr = avg.toFixed(1);
    }

    return {
      sessions: sessionsStr,
      participants: `${participants.length}`,
      // MED-1266: once saved every participant carries a recorded outcome, so coverage is
      // complete (marked === population); before save the rate is un-recorded.
      attendanceCoverage: {
        rate: attendanceSaved ? percentage : null,
        marked: attendanceSaved ? summary.attended + summary.partial + summary.absent : 0,
        population: participants.length,
      },
      engagement: engagementStr,
    };
  }, [sessionNumber, totalSessions, attendanceSaved, percentage, summary, participants.length, engagementRatingMap]);

  // Display status for header badge
  const displayStatus = useMemo(() => {
    if (attendanceSaved && feedbackSaved) return { label: 'Completed', color: 'green' };
    if (sessionStatus === 'completed') return { label: 'Completed', color: 'green' };
    if (sessionStatus === 'scheduled') return { label: 'Scheduled', color: 'blue' };
    return { label: sessionStatus || 'In Progress', color: 'yellow' };
  }, [sessionStatus, attendanceSaved, feedbackSaved]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!sessionId || !opened) return;

      try {
        setLoading(true);

        // MED-888: partner coaches now submit post-session feedback through the same
        // route (with the partner question set); fetch unconditionally.
        const [participantsRes, feedbackRes] = await Promise.all([
          coachPortalApi.getSessionParticipants(sessionId),
          coachPortalApi.getSessionFeedback(sessionId),
        ]);

        setSessionInfo({
          session_id: participantsRes.session_id,
          session_title: participantsRes.session_title,
          session_number: participantsRes.session_number,
          session_date: participantsRes.session_date,
          group_id: participantsRes.group_id,
          group_name: participantsRes.group_name,
        });
        setParticipants(participantsRes.participants);

        // Initialize attendance
        const initialMap = new Map<string, ParticipantAttendanceState>();
        participantsRes.participants.forEach((p) => {
          initialMap.set(p.user_id, initAttendanceState(p));
        });
        setAttendanceMap(initialMap);

        // Check if attendance already marked
        const hasMarkedAttendance = participantsRes.participants.some(
          (p) => p.current_status !== 'pending',
        );
        if (hasMarkedAttendance) {
          setAttendanceSaved(true);
          setIsEditMode(false);
        } else {
          setIsEditMode(true);
        }

        // Initialize feedback if existing (not applicable for partner coaches)
        if (feedbackRes.feedback) {
          setExistingFeedback(feedbackRes.feedback);
          setResponses(feedbackRes.feedback.responses);
          setFeedbackSaved(true);
          const existingFlags: CreateConcernFlagInput[] = feedbackRes.concernFlags.map((f) => ({
            participant_id: f.participant_id,
            concern_type: f.concern_type,
            notes: f.notes || undefined,
          }));
          setConcernFlags(existingFlags);
        }

        // Always load engagement ratings regardless of feedback status
        if (feedbackRes.engagementRatings?.length > 0) {
          const ratingMap = new Map<string, number>();
          feedbackRes.engagementRatings.forEach((r) => {
            ratingMap.set(r.participant_id, r.rating);
          });
          setEngagementRatingMap(ratingMap);
        }
      } catch (error) {
        console.error('Failed to fetch session data:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load session data.',
          color: 'red',
        });
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionId, opened, onClose]);

  // Reset state on close
  useEffect(() => {
    if (!opened) {
      setActiveTab('attendance');
      setAttendanceSaved(false);
      setFeedbackSaved(false);
      setIsEditMode(true);
      setExistingFeedback(null);
      setResponses({});
      setConcernFlags([]);
      setEngagementRatingMap(new Map());
    }
  }, [opened]);

  // MED-899: when the modal opens via the per-session feedback email's magic-auth
  // deep link (?session={sessionId}&pane=feedback), land directly on the feedback
  // tab and strip the pane param so refresh/back-navigation does not re-trigger it.
  useEffect(() => {
    if (!opened) return;
    if (searchParams.get('pane') !== 'feedback') return;
    setActiveTab('feedback');
    setSearchParams(
      (prev) => {
        prev.delete('pane');
        return prev;
      },
      { replace: true },
    );
  }, [opened, searchParams, setSearchParams]);

  const handleParticipantChange = useCallback(
    (userId: string, field: 'status' | 'absent_reason' | 'notes', value: string | null) => {
      setAttendanceMap((prev) => {
        const newMap = new Map(prev);
        const current = newMap.get(userId);
        if (!current) return prev;

        if (field === 'status') {
          const newStatus = value as AttendanceMarkingStatus;
          newMap.set(userId, {
            ...current,
            status: newStatus,
            absent_reason: newStatus === 'absent' ? current.absent_reason : null,
          });
          // Clear engagement rating when marking as absent
          if (newStatus === 'absent') {
            setEngagementRatingMap((prev) => {
              const next = new Map(prev);
              next.delete(userId);
              return next;
            });
          }
        } else if (field === 'absent_reason') {
          // Clear pill-based notes when reason changes, preserve manual notes
          const manualNotes = current.notes
            .split(',')
            .map((s) => s.trim())
            .filter((n) => n && !ALL_ABSENT_PILLS.includes(n))
            .join(', ');
          newMap.set(userId, {
            ...current,
            absent_reason: value as AbsentReason | null,
            notes: manualNotes,
          });
        } else if (field === 'notes') {
          newMap.set(userId, { ...current, notes: value || '' });
        }
        return newMap;
      });
    },
    [],
  );

  const validateAttendance = useCallback((): boolean => {
    for (const [userId, state] of attendanceMap) {
      if (state.status === 'absent' && !state.absent_reason) return false;
      if (state.status !== 'absent' && !engagementRatingMap.has(userId)) return false;
    }
    return true;
  }, [attendanceMap, engagementRatingMap]);

  const handleSubmitAttendance = useCallback(
    async (sendSurveys: boolean) => {
      if (!sessionId) return;

      if (!validateAttendance()) {
        const missingReasons = Array.from(attendanceMap.values()).some(
          (s) => s.status === 'absent' && !s.absent_reason,
        );
        const missingRatings = Array.from(attendanceMap.entries()).some(
          ([userId, s]) => s.status !== 'absent' && !engagementRatingMap.has(userId),
        );
        notifications.show({
          title: 'Validation Error',
          message: missingReasons
            ? 'Please select a reason for all absent participants.'
            : missingRatings
              ? 'Please set an engagement rating for all attending participants.'
              : 'Please complete all required fields.',
          color: 'red',
        });
        return;
      }

      try {
        setSubmittingAttendance(true);
        const attendance: ParticipantAttendanceInput[] = [];
        attendanceMap.forEach((state, userId) => {
          attendance.push({
            user_id: userId,
            status: state.status,
            absent_reason: state.status === 'absent' ? state.absent_reason : null,
            notes: state.notes || null,
          });
        });

        const engagement_ratings: EngagementRatingInput[] = [];
        engagementRatingMap.forEach((rating, userId) => {
          engagement_ratings.push({ participant_id: userId, rating });
        });

        await coachPortalApi.markAttendance(sessionId, {
          attendance,
          send_surveys: sendSurveys,
          engagement_ratings,
        });

        notifications.show({
          title: 'Attendance Saved',
          message: sendSurveys ? 'Surveys sent to participants.' : 'Attendance saved.',
          color: 'green',
        });

        setAttendanceSaved(true);
        setIsEditMode(false);
        setActiveTab('feedback');
      } catch (error) {
        console.error('Failed to submit attendance:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to submit attendance.',
          color: 'red',
        });
      } finally {
        setSubmittingAttendance(false);
      }
    },
    [sessionId, attendanceMap, engagementRatingMap, validateAttendance],
  );

  const handleSubmitFeedback = useCallback(async () => {
    if (!sessionId) return;

    // MED-888: validate required questions for the active question set (Medley or partner).
    const isMissing = (q: CoachFeedbackQuestion): boolean => {
      const value = responses[q.key];
      if (value === undefined || value === null) return true;
      if (typeof value === 'number') return Number.isNaN(value);
      return value.trim().length === 0;
    };
    const missingRequired = questionSet.questions.filter((q) => q.required && isMissing(q));
    if (missingRequired.length > 0) {
      notifications.show({
        title: 'Missing Required Responses',
        message: 'Please answer all required reflection questions.',
        color: 'orange',
      });
      return;
    }

    try {
      setSubmittingFeedback(true);

      // MED-888: partner coaches do NOT submit per-participant concern flags or
      // engagement ratings via this form — those stay on the attendance flow.
      const isPartnerSubmission = questionSet.id === 'partner';
      const engagement_ratings: EngagementRatingInput[] = [];
      if (!isPartnerSubmission) {
        engagementRatingMap.forEach((rating, userId) => {
          engagement_ratings.push({
            participant_id: userId,
            rating,
          });
        });
      }

      const payload = {
        responses,
        concern_flags: isPartnerSubmission ? [] : concernFlags,
        engagement_ratings,
      };

      if (existingFeedback) {
        await coachPortalApi.updateSessionFeedback(sessionId, payload);
      } else {
        await coachPortalApi.submitSessionFeedback(sessionId, payload);
      }

      notifications.show({
        title: 'Feedback Submitted',
        message: 'Your session feedback has been saved.',
        color: 'green',
      });

      setFeedbackSaved(true);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to submit feedback.',
        color: 'red',
      });
    } finally {
      setSubmittingFeedback(false);
    }
  }, [
    sessionId,
    responses,
    concernFlags,
    engagementRatingMap,
    existingFeedback,
    onSuccess,
    onClose,
    questionSet,
  ]);

  const handleSkipFeedback = () => {
    onSuccess?.();
    onClose();
  };

  const handleCancelEdit = () => {
    if (attendanceSaved) {
      // Revert to original attendance state from participants
      const revertedMap = new Map<string, ParticipantAttendanceState>();
      participants.forEach((p) => {
        revertedMap.set(p.user_id, initAttendanceState(p));
      });
      setAttendanceMap(revertedMap);
      setIsEditMode(false);
    }
  };

  // Use props or fetched sessionInfo for header
  const effectiveSessionNumber = sessionNumber ?? sessionInfo?.session_number ?? null;
  const effectiveTopicName = topicName ?? sessionInfo?.session_title ?? null;
  const effectiveGroupName = groupName ?? sessionInfo?.group_name ?? '';
  const effectiveScheduledStart = scheduledStart ?? sessionInfo?.session_date ?? null;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="80%"
      padding={0}
      withCloseButton={false}
      classNames={isCoachPersona ? { content: 'med-mobile-drawer med-post-session' } : undefined}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* MED-732: mobile-only floating close X (coach persona only). On desktop
          the header's inline close button is used instead. */}
      {isCoachPersona && (
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onClose}
          aria-label="Close"
          className="med-mobile-close"
          style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}
        >
          <X size={20} />
        </ActionIcon>
      )}
      {loading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : sessionInfo ? (
        <>
          <SessionDetailHeader
            sessionNumber={effectiveSessionNumber}
            topicName={effectiveTopicName}
            groupName={effectiveGroupName}
            scheduledStart={effectiveScheduledStart}
            timezone={timezone ?? null}
            displayStatus={displayStatus}
            stats={headerStats}
            coachName={coachName}
            onClose={onClose}
            hideCoach={isCoachPersona}
            hideCloseButton={isCoachPersona}
          />

          <Tabs value={activeTab} onChange={setActiveTab} className={styles.tabsContainer}>
            <Tabs.List className={`${styles.tabsList} med-post-session-tabs`}>
                <Tabs.Tab
                  value="attendance"
                  leftSection={<CheckCircle size={14} />}
                  rightSection={
                    attendanceSaved ? (
                      <ThemeIcon size="xs" variant="filled" color="green" radius="xl">
                        <Check size={10} />
                      </ThemeIcon>
                    ) : undefined
                  }
                >
                  Attendance
                </Tabs.Tab>
                {/* MED-888: feedback tab is now available to partner coaches too —
                    they see the partner question set rather than the Medley set. */}
                <Tabs.Tab
                  value="feedback"
                  leftSection={<MessageCircle size={14} />}
                  rightSection={
                    feedbackSaved ? (
                      <ThemeIcon size="xs" variant="filled" color="green" radius="xl">
                        <Check size={10} />
                      </ThemeIcon>
                    ) : undefined
                  }
                >
                  Session Feedback
                </Tabs.Tab>
                {!isPartnerCoach && (
                  <Tabs.Tab
                    value="notes"
                    leftSection={<Edit3 size={14} />}
                  >
                    My Notes
                  </Tabs.Tab>
                )}
              </Tabs.List>

            <ScrollArea className={styles.tabContent}>
              <div style={{ padding: 'var(--mantine-spacing-md)' }}>
              {/* Attendance Tab */}
              <Tabs.Panel value="attendance">
                <Stack gap="md">
                  {/* Submitted banner (view mode only) */}
                  {attendanceSaved && !isEditMode && (
                    <Alert icon={<CheckCircle size={16} />} color="green" variant="light">
                      <Text size="sm">Attendance has been submitted</Text>
                    </Alert>
                  )}

                  {/* Attendance summary pills + percentage */}
                  <Box
                    p="sm"
                    style={{
                      backgroundColor: 'var(--mantine-color-gray-0)',
                      borderRadius: 'var(--mantine-radius-sm)',
                    }}
                  >
                    <Group justify="space-between">
                      <Group gap="md">
                        <Badge color="green" variant="light">
                          {summary.attended} Attended
                        </Badge>
                        <Badge color="yellow" variant="light">
                          {summary.partial} Partial
                        </Badge>
                        <Badge color="red" variant="light">
                          {summary.absent} Absent
                        </Badge>
                      </Group>
                      <Text fw={600}>{percentage}% Attendance</Text>
                    </Group>
                  </Box>

                  {/* Participant table */}
                  {participants.length > 0 ? (
                    <Table className="med-post-session-table">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Participant</Table.Th>
                          <Table.Th>RSVP</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th>Reason</Table.Th>
                          <Table.Th>Notes</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      {participants.map((participant) => {
                          const attendance = attendanceMap.get(participant.user_id);
                          if (!attendance) return null;
                          const isAbsent = attendance.status === 'absent';
                          const activePills =
                            attendance.status === 'partial'
                              ? PARTIAL_ATTENDANCE_PILLS
                              : attendance.absent_reason
                                ? ABSENT_PILLS_BY_REASON[attendance.absent_reason]
                                : [];
                          const isWithdrawn = participant.participant_status === 'withdrawn';
                          return (
                            <Table.Tbody key={participant.user_id} className="med-post-session-group">
                            <Table.Tr
                              className="med-post-session-participant"
                              style={isWithdrawn ? { opacity: 0.5 } : undefined}
                            >
                              <Table.Td className="med-pp-name">
                                <Stack gap={0}>
                                  <Group gap="xs">
                                    <Text size="sm" fw={500}>
                                      {participant.first_name} {participant.last_name}
                                    </Text>
                                    {isWithdrawn && (
                                      <Badge size="xs" variant="filled" styles={{ root: { backgroundColor: '#E8E8E8', color: '#444444', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600 } }}>
                                        WITHDRAWN
                                      </Badge>
                                    )}
                                  </Group>
                                  <Text size="xs" c="dimmed">
                                    {participant.email}
                                  </Text>
                                </Stack>
                              </Table.Td>
                              <Table.Td className="med-pp-rsvp">
                                {(() => {
                                  const rsvpConfig = RSVP_CONFIG[participant.rsvp_status] || RSVP_CONFIG.pending;
                                  const RsvpIcon = rsvpConfig.icon;
                                  return (
                                    <Badge
                                      size="sm"
                                      variant="light"
                                      color={rsvpConfig.color}
                                      leftSection={<RsvpIcon size={12} />}
                                    >
                                      {rsvpConfig.label}
                                    </Badge>
                                  );
                                })()}
                              </Table.Td>
                              <Table.Td className="med-pp-status">
                                {!isEditMode ? (
                                  <Badge
                                    color={STATUS_COLORS[attendance.status]}
                                    variant="light"
                                  >
                                    {attendance.status.charAt(0).toUpperCase() +
                                      attendance.status.slice(1)}
                                  </Badge>
                                ) : (
                                  <>
                                    <Select
                                      size="xs"
                                      data={ATTENDANCE_STATUS_OPTIONS}
                                      value={attendance.status}
                                      onChange={(value) =>
                                        handleParticipantChange(
                                          participant.user_id,
                                          'status',
                                          value,
                                        )
                                      }
                                      style={{ width: 120 }}
                                      className="med-only-desktop"
                                    />
                                    <UnstyledButton
                                      className="med-only-mobile med-mobile-picker-trigger"
                                      onClick={() =>
                                        setPicker({ type: 'status', userId: participant.user_id })
                                      }
                                    >
                                      <span className="med-picker-label">Status</span>
                                      <span className="med-picker-value">
                                        {ATTENDANCE_STATUS_OPTIONS.find((o) => o.value === attendance.status)?.label || '—'}
                                      </span>
                                      <ChevronDown size={16} />
                                    </UnstyledButton>
                                  </>
                                )}
                              </Table.Td>
                              <Table.Td className="med-pp-reason">
                                {!isEditMode ? (
                                  <Text size="sm">
                                    {attendance.status === 'absent'
                                      ? ABSENT_REASON_OPTIONS.find(
                                          (o) => o.value === attendance.absent_reason,
                                        )?.label || '—'
                                      : '—'}
                                  </Text>
                                ) : (
                                  <>
                                    <Select
                                      size="xs"
                                      data={ABSENT_REASON_OPTIONS}
                                      value={
                                        attendance.status === 'absent'
                                          ? attendance.absent_reason || ''
                                          : ''
                                      }
                                      onChange={(value) =>
                                        handleParticipantChange(
                                          participant.user_id,
                                          'absent_reason',
                                          value,
                                        )
                                      }
                                      disabled={attendance.status !== 'absent'}
                                      placeholder={
                                        attendance.status === 'absent'
                                          ? 'Select reason'
                                          : 'N/A — mark absent first'
                                      }
                                      clearable
                                      style={{ width: 150 }}
                                      className="med-only-desktop"
                                    />
                                    <UnstyledButton
                                      className="med-only-mobile med-mobile-picker-trigger"
                                      disabled={attendance.status !== 'absent'}
                                      onClick={() => {
                                        if (attendance.status !== 'absent') return;
                                        setPicker({ type: 'reason', userId: participant.user_id });
                                      }}
                                      data-disabled={attendance.status !== 'absent' ? 'true' : undefined}
                                    >
                                      <span className="med-picker-label">Reason</span>
                                      <span className="med-picker-value">
                                        {attendance.status === 'absent'
                                          ? ABSENT_REASON_OPTIONS.find((o) => o.value === attendance.absent_reason)?.label || 'Select reason'
                                          : 'N/A — mark absent first'}
                                      </span>
                                      <ChevronDown size={16} />
                                    </UnstyledButton>
                                  </>
                                )}
                              </Table.Td>
                              <Table.Td className="med-pp-notes">
                                {!isEditMode ? (
                                  <Text size="sm">{attendance.notes || '—'}</Text>
                                ) : (
                                  <Stack gap={4}>
                                    {activePills.length > 0 && (
                                      <Chip.Group
                                        multiple
                                        value={activePills.filter((pill) =>
                                          attendance.notes
                                            .split(',')
                                            .map((s) => s.trim())
                                            .includes(pill),
                                        )}
                                        onChange={(selected: string[]) => {
                                          // Preserve manually typed notes (not from pills)
                                          const currentNotes = attendance.notes
                                            .split(',')
                                            .map((s) => s.trim())
                                            .filter((n) => n && !activePills.includes(n));
                                          const combined = [
                                            ...selected,
                                            ...currentNotes,
                                          ].filter(Boolean);
                                          handleParticipantChange(
                                            participant.user_id,
                                            'notes',
                                            combined.join(', '),
                                          );
                                        }}
                                      >
                                        <Group gap={6} className="med-only-desktop">
                                          {activePills.map((pill) => (
                                            <Chip key={pill} value={pill} size="sm" variant="outline">
                                              {pill}
                                            </Chip>
                                          ))}
                                        </Group>
                                      </Chip.Group>
                                    )}
                                    {/* MED-732: notes hidden behind "+ Add Optional Note" button
                                        until the coach opts in (or if there are already notes). */}
                                    {notesRevealed.has(participant.user_id) || attendance.notes ? (
                                      <Textarea
                                        size="xs"
                                        value={attendance.notes}
                                        onChange={(e) =>
                                          handleParticipantChange(
                                            participant.user_id,
                                            'notes',
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Optional notes"
                                        rows={3}
                                        autosize={false}
                                        style={{ width: '100%' }}
                                      />
                                    ) : (
                                      <Button
                                        variant="subtle"
                                        size="xs"
                                        color="gray"
                                        onClick={() => revealNotes(participant.user_id)}
                                        style={{ alignSelf: 'flex-start' }}
                                      >
                                        + Add Optional Note
                                      </Button>
                                    )}
                                  </Stack>
                                )}
                              </Table.Td>
                            </Table.Tr>
                            {!isAbsent && (
                              <Table.Tr
                                className="med-post-session-engagement"
                                style={{ borderTop: 'none' }}
                              >
                                <Table.Td colSpan={5} py={6} pl="xl">
                                  <div className="med-only-desktop">
                                    <ParticipantEngagementRating
                                      rating={engagementRatingMap.get(participant.user_id) ?? null}
                                      onRatingChange={(rating) =>
                                        setEngagementRatingMap((prev) => {
                                          const next = new Map(prev);
                                          next.set(participant.user_id, rating);
                                          return next;
                                        })
                                      }
                                      readOnly={!isEditMode}
                                    />
                                  </div>
                                  <UnstyledButton
                                    className="med-only-mobile med-mobile-picker-trigger"
                                    disabled={!isEditMode}
                                    onClick={() => {
                                      if (!isEditMode) return;
                                      setPicker({ type: 'engagement', userId: participant.user_id });
                                    }}
                                  >
                                    <span className="med-picker-label">Engagement Level</span>
                                    <span className="med-picker-value">
                                      {(() => {
                                        const r = engagementRatingMap.get(participant.user_id);
                                        if (r == null) return 'Not rated';
                                        const info = ENGAGEMENT_RATING_MAP.get(r);
                                        return info ? `${r} — ${info.label}` : String(r);
                                      })()}
                                    </span>
                                    <ChevronDown size={16} />
                                  </UnstyledButton>
                                </Table.Td>
                              </Table.Tr>
                            )}
                            </Table.Tbody>
                          );
                        })}
                    </Table>
                  ) : (
                    <Stack align="center" gap="md" py="xl">
                      <AlertCircle size={32} color="var(--mantine-color-gray-5)" />
                      <Text c="dimmed">No participants in this session.</Text>
                    </Stack>
                  )}

                  {/* Footer buttons */}
                  <Group justify="space-between">
                    {!isEditMode ? (
                      <>
                        <div />
                        <Button variant="light" onClick={() => setIsEditMode(true)}>
                          Edit Attendance
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="subtle"
                          color="gray"
                          onClick={attendanceSaved ? handleCancelEdit : onClose}
                        >
                          Cancel
                        </Button>
                        <Group gap="sm">
                          <Button
                            variant="outline"
                            onClick={() => handleSubmitAttendance(false)}
                            loading={submittingAttendance}
                            disabled={participants.length === 0}
                          >
                            Save Only
                          </Button>
                          <Button
                            onClick={() => setConfirmSendSurveys(true)}
                            loading={submittingAttendance}
                            disabled={participants.length === 0}
                          >
                            Save & Send Surveys
                          </Button>
                        </Group>
                      </>
                    )}
                  </Group>
                </Stack>
              </Tabs.Panel>

              {/* Feedback Tab — MED-888: partner coaches see the partner question set;
                  Medley coaches see the existing question set unchanged. */}
              <Tabs.Panel value="feedback">
                <Stack gap="md">
                  {!attendanceSaved && (
                    <Alert color="yellow" variant="light" icon={<AlertCircle size={18} />}>
                      <Text size="sm">
                        Please mark attendance first before submitting session feedback.
                      </Text>
                    </Alert>
                  )}

                  <Text fw={600} size="sm">
                    {questionSet.sectionHeader(effectiveSessionNumber)}
                  </Text>

                  {questionSet.questions.map((q) => {
                    const labelNode = (
                      <Group gap={4}>
                        <Text size="sm" fw={500}>
                          {q.questionText}
                        </Text>
                        {q.required && (
                          <Text size="sm" c="red" fw={500}>
                            *
                          </Text>
                        )}
                      </Group>
                    );

                    if (q.type === 'singleSelect') {
                      const numericValue =
                        typeof responses[q.key] === 'number'
                          ? (responses[q.key] as number)
                          : typeof responses[q.key] === 'string' && responses[q.key] !== ''
                            ? Number(responses[q.key])
                            : null;
                      return (
                        <Radio.Group
                          key={q.key}
                          label={labelNode}
                          value={numericValue !== null ? String(numericValue) : ''}
                          onChange={(value) => {
                            setResponses((prev) => ({
                              ...prev,
                              [q.key]: value === '' ? '' : Number(value),
                            }));
                          }}
                        >
                          <Stack gap="xs" mt="xs">
                            {(q.options ?? []).map((opt) => (
                              <Radio
                                key={opt.value}
                                value={String(opt.value)}
                                label={
                                  <Stack gap={2}>
                                    <Text size="sm">
                                      ({opt.value}) {opt.label}
                                    </Text>
                                    {opt.description && (
                                      <Text size="xs" c="dimmed" fs="italic">
                                        {opt.description}
                                      </Text>
                                    )}
                                  </Stack>
                                }
                              />
                            ))}
                          </Stack>
                        </Radio.Group>
                      );
                    }

                    const currentValue =
                      typeof responses[q.key] === 'string'
                        ? (responses[q.key] as string)
                        : responses[q.key] !== undefined && responses[q.key] !== null
                          ? String(responses[q.key])
                          : '';

                    if (q.charLimit) {
                      return (
                        <CharacterCountTextarea
                          key={q.key}
                          label={labelNode}
                          placeholder={q.placeholder}
                          value={currentValue}
                          charLimit={q.charLimit}
                          helperLine={q.helperText}
                          onChange={(val) =>
                            setResponses((prev) => ({ ...prev, [q.key]: val }))
                          }
                          minRows={3}
                          autosize
                          maxRows={6}
                        />
                      );
                    }

                    return (
                      <Textarea
                        key={q.key}
                        label={labelNode}
                        placeholder={q.placeholder}
                        value={currentValue}
                        onChange={(e) => {
                          const val = e.currentTarget.value;
                          setResponses((prev) => ({ ...prev, [q.key]: val }));
                        }}
                        minRows={2}
                        autosize
                        maxRows={5}
                      />
                    );
                  })}

                  {/* Concern Flags — Medley coaches only. Partner coaches don't flag
                      participants from this form (per MED-888 out-of-scope). */}
                  {!isPartnerCoach && (
                    <>
                      <Divider my="sm" />
                      <ConcernFlagsSection
                        participants={participants}
                        flags={concernFlags}
                        onFlagsChange={setConcernFlags}
                      />
                    </>
                  )}

                  {/* Submit */}
                  <Group justify="space-between" mt="md">
                    <Button variant="subtle" color="gray" size="xs" onClick={handleSkipFeedback}>
                      Skip for Now
                    </Button>
                    <Button
                      onClick={handleSubmitFeedback}
                      loading={submittingFeedback}
                      leftSection={<CheckCircle size={16} />}
                    >
                      {existingFeedback ? 'Update Feedback' : 'Submit Feedback'}
                    </Button>
                  </Group>
                </Stack>
              </Tabs.Panel>

              {/* My Notes Tab (hidden for partner coaches) */}
              {!isPartnerCoach && (
                <Tabs.Panel value="notes">
                  {sessionId && <MyNotesPanel sessionId={sessionId} />}
                </Tabs.Panel>
              )}
              </div>
            </ScrollArea>
          </Tabs>
        </>
      ) : (
        <Center py="xl">
          <Text c="dimmed">Failed to load session data.</Text>
        </Center>
      )}
      {/* MED-646: Confirm survey send dialog */}
      <Modal
        opened={confirmSendSurveys}
        onClose={() => setConfirmSendSurveys(false)}
        title="Confirm Save & Send Surveys"
        size="sm"
        centered
        zIndex={301}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              You are about to save attendance and send surveys to{' '}
              <Text component="span" fw={700}>
                {summary.attended + summary.partial} participant{summary.attended + summary.partial !== 1 ? 's' : ''}
              </Text>
              . This action cannot be undone.
            </Text>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setConfirmSendSurveys(false)}
              disabled={submittingAttendance}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => {
                setConfirmSendSurveys(false);
                handleSubmitAttendance(true);
              }}
              loading={submittingAttendance}
              disabled={submittingAttendance}
            >
              Confirm &amp; Send
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* MED-732: mobile picker modal (replaces dropdowns with tap-to-open sheet).
          Status and Reason pickers also expose the relevant quick-note pills
          inline (below the options), so the coach doesn't have to close the
          picker to flag "Meeting conflict" / "Work travel" / "Arrived late"
          / "Left early". Engagement picker auto-closes (single choice). */}
      {(() => {
        if (picker === null) {
          return (
            <Modal opened={false} onClose={closePicker}>
              <span />
            </Modal>
          );
        }
        const attendance = attendanceMap.get(picker.userId);
        const currentStatus = attendance?.status;
        const currentReason = attendance?.absent_reason;
        const currentRating = engagementRatingMap.get(picker.userId);

        // Status modal now folds in reason + quick-note pills inline so the
        // coach doesn't need a separate reason picker step.
        const partialPills =
          picker.type === 'status' && currentStatus === 'partial'
            ? PARTIAL_ATTENDANCE_PILLS
            : [];
        const reasonPills =
          picker.type === 'status' && currentStatus === 'absent' && currentReason
            ? ABSENT_PILLS_BY_REASON[currentReason]
            : [];

        const title =
          picker.type === 'status'
            ? 'Attendance'
            : picker.type === 'engagement'
              ? 'Engagement Level'
              : '';

        return (
          <Modal
            opened={picker !== null}
            onClose={closePicker}
            title={title}
            centered
            size="sm"
            classNames={{ content: 'med-mobile-modal' }}
          >
            <Stack gap={8}>
              {picker.type === 'status' && (
                <>
                  {ATTENDANCE_STATUS_OPTIONS.map((opt) => {
                    const isSelected = currentStatus === opt.value;
                    return (
                      <UnstyledButton
                        key={opt.value}
                        className={`med-picker-option${isSelected ? ' med-picker-option--selected' : ''}`}
                        onClick={() => handleParticipantChange(picker.userId, 'status', opt.value)}
                      >
                        {opt.label}
                      </UnstyledButton>
                    );
                  })}

                  {/* Reason section — only shown after Absent is selected. */}
                  {currentStatus === 'absent' && (
                    <>
                      <Divider my="xs" />
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.3px' }}>
                        Reason
                      </Text>
                      {ABSENT_REASON_OPTIONS.map((opt) => {
                        const isSelected = currentReason === opt.value;
                        return (
                          <UnstyledButton
                            key={opt.value}
                            className={`med-picker-option${isSelected ? ' med-picker-option--selected' : ''}`}
                            onClick={() =>
                              handleParticipantChange(picker.userId, 'absent_reason', opt.value)
                            }
                          >
                            {opt.label}
                          </UnstyledButton>
                        );
                      })}
                    </>
                  )}
                </>
              )}

              {picker.type === 'engagement' &&
                ENGAGEMENT_RATING_CONFIG.map((cfg) => {
                  const isSelected = currentRating === cfg.value;
                  return (
                    <UnstyledButton
                      key={cfg.value}
                      className={`med-picker-option${isSelected ? ' med-picker-option--selected' : ''}`}
                      onClick={() => {
                        const uid = picker.userId;
                        setEngagementRatingMap((prev) => {
                          const next = new Map(prev);
                          next.set(uid, cfg.value);
                          return next;
                        });
                        closePicker();
                      }}
                    >
                      <span style={{ fontWeight: 600, marginRight: 8 }}>{cfg.value}</span>
                      {cfg.label}
                    </UnstyledButton>
                  );
                })}

              {/* Quick-note pills: revealed when partial or a reason is picked. */}
              {(partialPills.length > 0 || reasonPills.length > 0) && (
                <>
                  <Divider my="xs" />
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.3px' }}>
                    Quick Notes
                  </Text>
                  <Group gap={8} wrap="wrap">
                    {[...partialPills, ...reasonPills].map((pill) => {
                      const isOn = isPillActive(picker.userId, pill);
                      return (
                        <UnstyledButton
                          key={pill}
                          className={`med-picker-pill${isOn ? ' med-picker-pill--selected' : ''}`}
                          onClick={() => togglePillInNotes(picker.userId, pill)}
                        >
                          {pill}
                        </UnstyledButton>
                      );
                    })}
                  </Group>
                </>
              )}

              {picker.type === 'status' && (
                <Button fullWidth color="medleyGreen" onClick={closePicker} mt="sm">
                  Done
                </Button>
              )}
            </Stack>
          </Modal>
        );
      })()}
    </Drawer>
  );
}

export default PostSessionModal;
